// ChildProfilePhotoEditor — small inline control to upload, replace, or
// clear a child's profile (identity) photo. RLS on the storage bucket
// scopes who can write; this component just trusts the bucket policy.
//
// Used by:
//   • Parent dashboard / preferences  (parent owns identity)
//   • Teacher roster row sheet        (teacher of covered classroom)
//   • Admin /admin/children sheet     (admin of org)

import { useRef, useState } from "react";
import { Camera, TrashSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import ChildAvatar from "@/components/children/ChildAvatar";
import {
  clearChildProfilePhoto,
  uploadChildProfilePhoto,
} from "@/lib/childProfilePhoto";

/**
 * <ChildProfilePhotoEditor
 *   child={{ id, first_name, last_name, profile_photo_path }}
 *   onChanged={(newPath) => ...}      // optional callback after success
 *   testId="child-photo-editor-<id>"  // optional explicit testid
 * />
 */
export default function ChildProfilePhotoEditor({ child, onChanged, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  if (!child?.id) return null;

  const tid = testId || `child-photo-editor-${child.id}`;
  const name =
    [child.first_name, child.last_name].filter(Boolean).join(" ") ||
    "Child";

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const newPath = await uploadChildProfilePhoto({
        childId: child.id,
        file,
        oldPath: child.profile_photo_path || null,
      });
      toast.success("Profile photo updated");
      onChanged?.(newPath);
    } catch (err) {
      toast.error(err.message || "Could not upload photo");
    } finally {
      setBusy(false);
    }
  };

  const onClear = async () => {
    if (!child.profile_photo_path) return;
    if (!window.confirm("Remove the profile photo?")) return;
    setBusy(true);
    try {
      await clearChildProfilePhoto({
        childId: child.id,
        oldPath: child.profile_photo_path,
      });
      toast.success("Profile photo removed");
      onChanged?.(null);
    } catch (err) {
      toast.error(err.message || "Could not remove photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={tid} className="flex items-center gap-3">
      <ChildAvatar
        path={child.profile_photo_path}
        name={name}
        size={64}
        testId={`${tid}-avatar`}
      />
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
          Profile photo
        </p>
        <p className="mt-0.5 text-[12px] text-neutral-600">
          A single identity image for {name}. This is separate from
          classroom/activity photos.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid={`${tid}-upload`}
            onClick={onPick}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-white px-3 py-1.5 text-xs font-semibold text-primary-hover hover:bg-primary/5 disabled:opacity-60"
          >
            <Camera size={12} weight="duotone" />
            {busy
              ? "Uploading…"
              : child.profile_photo_path
                ? "Replace photo"
                : "Upload photo"}
          </button>
          {child.profile_photo_path && (
            <button
              type="button"
              data-testid={`${tid}-clear`}
              onClick={onClear}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-60"
            >
              <TrashSimple size={12} weight="bold" />
              Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={onFile}
          data-testid={`${tid}-input`}
        />
      </div>
    </div>
  );
}
